import { APP_NAME, APP_VERSION, SUPPORT_PAGE_URL, SUPPORT_URL } from './constants';

export type LegalSection = {
  heading?: string;
  paragraphs?: string[];
  bullets?: string[];
};

export type LegalDocument = {
  id: string;
  title: string;
  lastUpdated?: string;
  sections: LegalSection[];
};

export const LEGAL_DOCUMENTS = {
  privacy: {
    id: 'privacy',
    title: 'Privacy Policy',
    lastUpdated: 'June 9, 2026',
    sections: [
      {
        paragraphs: [
          `${APP_NAME} (${APP_VERSION}) is designed with privacy in mind.`,
        ],
      },
      {
        heading: 'Summary',
        bullets: [
          'No user accounts are created.',
          'No cloud storage is used for communication content.',
          'No advertising or data sales.',
          "Speech uses your device's built in offline voices.",
          'Parent settings are protected by a parent PIN.',
        ],
      },
      {
        heading: 'Information stored on your device',
        paragraphs: [
          `${APP_NAME} may store the following locally on your device only:`,
        ],
        bullets: [
          'Parent settings (display options, routines, schedule, voice, speech speed)',
          'Parent PIN',
          'Favorite usage counts',
          'Custom buttons created by a parent (label, phrase, and photo)',
        ],
      },
      {
        heading: 'Photo library access',
        paragraphs: [
          'If a parent chooses to create a custom button, the app requests access to the photo library only for that action. Photos selected by the parent are copied into the app\'s private on device storage for the custom button.',
        ],
      },
      {
        heading: 'Speech',
        paragraphs: [
          "Spoken phrases use the device's built in text to speech system. Voice audio is generated on the device and is not sent to Azalea servers.",
        ],
      },
      {
        heading: 'Privacy for all ages',
        paragraphs: [
          `${APP_NAME} may be used by kids, teens, and adults with caregiver supervision. Caregivers control settings through a parent PIN.`,
        ],
      },
      {
        heading: 'Data sharing',
        paragraphs: [
          'We do not sell personal information. We do not share app data with third parties for advertising or tracking.',
        ],
      },
      {
        heading: 'Data retention and deletion',
        paragraphs: [
          `Because ${APP_NAME} does not create online accounts, there is no online account to delete. You can delete all app data at any time in Parent Settings > Data & Privacy > Delete All App Data. This removes custom buttons, settings, favorites usage, and the parent PIN from the device.`,
        ],
      },
      {
        heading: 'Changes',
        paragraphs: [
          'We may update this policy as the app changes. Updated versions will be available in the app and on this website.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          `For privacy questions, visit our Support page or open an issue at ${SUPPORT_URL}.`,
        ],
      },
    ],
  } satisfies LegalDocument,

  terms: {
    id: 'terms',
    title: 'Terms of Use',
    lastUpdated: 'June 17, 2026',
    sections: [
      {
        paragraphs: [
          `These Terms of Use ("Terms") govern your use of the ${APP_NAME} mobile application (${APP_VERSION}). By downloading, accessing, or using ${APP_NAME}, you agree to these Terms and our Privacy Policy. If you do not agree, do not use the app.`,
        ],
      },
      {
        heading: 'Purpose',
        paragraphs: [
          `${APP_NAME} is a picture based communication support tool. It is not a medical device and does not provide medical, therapeutic, diagnostic, or professional advice. It does not replace speech therapy, occupational therapy, or other professional services.`,
        ],
      },
      {
        heading: 'Parent and caregiver responsibility',
        paragraphs: [
          'A parent, legal guardian, or authorized caregiver should supervise setup and use of the app, including custom buttons, parent settings, voice selection, and photo library access. You are responsible for how the app is used on your device.',
        ],
      },
      {
        heading: 'Acceptable use',
        paragraphs: [
          'You agree to use the app lawfully and only add photos or other content you have the right to use. You may not use the app to harass, harm, or violate the rights of others, or to upload unlawful or offensive content.',
        ],
      },
      {
        heading: 'No online accounts',
        paragraphs: [
          `${APP_NAME} does not create online user accounts or cloud profiles. You are responsible for device security, including your parent PIN and any device passcode or biometric lock.`,
        ],
      },
      {
        heading: 'Azalea Premium and subscriptions',
        paragraphs: [
          'Everyday communication in Azalea is free. Optional Azalea Premium features are available through an auto-renewable subscription billed by Apple via the App Store. Pricing, billing period, and renewal terms are shown at purchase.',
          'Payment, refunds, and subscription management are handled by Apple according to Apple\'s policies. You can cancel in iOS Settings → Apple ID → Subscriptions. Access to Premium features continues until the end of the current billing period after cancellation.',
        ],
      },
      {
        heading: 'Intellectual property',
        paragraphs: [
          `The app, including its design, software, built in pictures, and documentation, is owned by ${APP_NAME}'s developer or licensors and protected by intellectual property laws. You may not copy, modify, distribute, reverse engineer, or create derivative works except as permitted by law or these Terms.`,
        ],
      },
      {
        heading: 'Disclaimer of warranties',
        paragraphs: [
          'The app is provided "as is" and "as available" without warranties of any kind, whether express or implied. We do not guarantee uninterrupted, error free, or secure operation.',
        ],
      },
      {
        heading: 'Limitation of liability',
        paragraphs: [
          'To the fullest extent permitted by law, the developer and its affiliates are not liable for indirect, incidental, special, consequential, or punitive damages arising from use of the app. Our total liability for any claim relating to the app shall not exceed the amount you paid for Azalea Premium in the twelve months before the claim, or one hundred U.S. dollars (USD $100), whichever is greater.',
        ],
      },
      {
        heading: 'Changes to these Terms',
        paragraphs: [
          'We may update these Terms as the app changes. Material updates will be reflected by changing the "Last updated" date above and, when appropriate, in the app. Continued use after updates become effective means you accept the revised Terms where permitted by law.',
        ],
      },
      {
        heading: 'Governing law',
        paragraphs: [
          'These Terms are governed by the laws of the United States and the State of Delaware, without regard to conflict of law principles, except where mandatory consumer protection laws in your country provide otherwise.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          `For questions about these Terms, visit our Support page or open an issue at ${SUPPORT_URL}.`,
        ],
      },
    ],
  } satisfies LegalDocument,

  faq: {
    id: 'faq',
    title: 'FAQ',
    sections: [
      {
        heading: `What is ${APP_NAME}?`,
        paragraphs: [
          `${APP_NAME} is a picture based communication app designed with autistic communicators in mind. Users tap images to speak short phrases using the device's built in text to speech voices. It works for kids, teens, and adults.`,
        ],
      },
      {
        heading: `Does ${APP_NAME} need the internet?`,
        paragraphs: [
          'No. Core communication, voices, routines, and favorites work completely offline after install.',
        ],
      },
      {
        heading: `Does ${APP_NAME} create an account?`,
        paragraphs: [
          `No. ${APP_NAME} does not create user accounts or online profiles. Premium is an optional in app subscription billed through Apple.`,
        ],
      },
      {
        heading: 'Is Azalea free?',
        paragraphs: [
          'Free to communicate, forever. No subscription required for everyday communication.',
        ],
      },
      {
        heading: 'What is Azalea Premium?',
        paragraphs: [
          'Azalea Premium adds advanced features in the app. Everyday communication stays free with or without it.',
        ],
      },
      {
        heading: 'How do I add custom buttons?',
        paragraphs: [
          'Custom photo buttons are part of Azalea Premium. Open Parent Settings, subscribe to Premium in the app, then choose a photo, add a label and spoken phrase, and save the button.',
        ],
      },
      {
        heading: 'How do I change the voice or speed?',
        paragraphs: [
          'Open Parent Settings, choose a voice from the list (preview first if you like), and set Speech speed.',
        ],
      },
      {
        heading: 'How do I remove stored app data?',
        paragraphs: [
          'Open Parent Settings, scroll to Data & Privacy, and tap Delete All App Data.',
        ],
      },
      {
        heading: 'Who is this app for?',
        paragraphs: [
          `${APP_NAME} supports kids, teens, and adults who benefit from picture based communication, with caregiver controlled settings. It is especially helpful for people who are non speaking, minimally speaking, or find speech hard in the moment. Anyone who benefits from visual communication can use it.`,
        ],
      },
      {
        heading: 'Is Azalea a medical device or therapy?',
        paragraphs: [
          'No. Azalea is a communication support tool, not a medical device. It does not replace speech therapy, occupational therapy, or professional diagnosis.',
        ],
      },
    ],
  } satisfies LegalDocument,

  support: {
    id: 'support',
    title: 'Contact Support',
    sections: [
      {
        paragraphs: [
          `Need help with ${APP_NAME}? We're here.`,
          `Email us at contact@azaleaspeak.com or use the support link below.`,
        ],
      },
      {
        heading: 'Support options',
        bullets: [
          'Email contact@azaleaspeak.com with your question.',
          'Open a support issue on GitHub (link below).',
          'Include your device model and iOS version when reporting a problem.',
          'Describe what you expected and what happened instead.',
        ],
      },
      {
        heading: 'Account deletion',
        paragraphs: [
          `${APP_NAME} does not create user accounts. If you want to remove all information stored by the app on this device, use Delete All App Data in Parent Settings.`,
        ],
      },
      {
        heading: 'Support page',
        paragraphs: [
          SUPPORT_PAGE_URL,
          SUPPORT_URL,
        ],
      },
    ],
  } satisfies LegalDocument,

  dataRights: {
    id: 'dataRights',
    title: 'Data & Privacy Rights',
    sections: [
      {
        heading: `Your data rights in ${APP_NAME}`,
      },
      {
        heading: 'No online account',
        paragraphs: [
          `${APP_NAME} does not require sign in and does not create online user accounts. There is no cloud profile to delete.`,
        ],
      },
      {
        heading: 'What is stored on this device',
        bullets: [
          'Parent settings',
          'Parent PIN',
          'Favorite usage counts',
          'Custom buttons and photos added by a parent',
        ],
      },
      {
        heading: 'Delete all app data',
        paragraphs: [
          `You can permanently delete all app data stored by ${APP_NAME} on this device using Delete All App Data in Parent Settings.`,
          'This action removes custom buttons, settings, favorites usage, and the parent PIN from the device. Built in communication pictures included with the app are not affected.',
        ],
      },
      {
        heading: 'Apple App Store compliance',
        paragraphs: [
          'Because no account is created, account deletion is provided as on device data deletion. This meets the requirement to let users remove data associated with their use of the app.',
        ],
      },
    ],
  } satisfies LegalDocument,
} as const;
